/**
 * Delivery send-log data — shared types, event/source metadata, and sort
 * helpers for Messaging → Delivery.
 *
 * Data source: the LIVE notification-history ops (listMessageHistory /
 * getMessageHistoryDetail) via message-history-api.ts. The fictional
 * DELIVERY_ROWS/_DGEN_* seed was DELETED 2026-07-18 per the Voyage docket
 * §2.1(b) — no baked send-log content remains in this module.
 */
import { MSG_CHANNEL } from "./messaging-shared";
import { t as i18nT } from "../../lib/i18n";

export interface DelivEvent { type: string; at: string; meta?: string; }
export interface DeliveryRow {
  id: string; channel: string;
  sender: { name: string; from: string };
  recipient: { name: string; to: string };
  source: { kind: string; label: string };
  subject?: string; text?: string; html?: string;
  sent: string; events: DelivEvent[];
  /* Notification-history provenance (listMessageHistory / getMessageHistoryDetail). */
  displayId?: string; status?: string; provider?: string; credentialSource?: string;
  replyTo?: string; bodyUnavailable?: boolean;
}

export const DELIV_EVENTS: Record<string, { label: string; labelKey: string; tone: string; icon: string; blurb: string }> = {
  send: { labelKey: "messaging.deliveryData.sent", get label() { return i18nT("messaging.deliveryData.sent"); }, tone: "neutral", icon: "send", blurb: "Accepted by the sending gateway." },
  delivered: { labelKey: "messaging.deliveryData.delivered", get label() { return i18nT("messaging.deliveryData.delivered"); }, tone: "success", icon: "check", blurb: "Confirmed delivered to the recipient's inbox / handset." },
  opened: { labelKey: "messaging.deliveryData.opened", get label() { return i18nT("messaging.deliveryData.opened"); }, tone: "info", icon: "eye", blurb: "Recipient opened the message." },
  clicked: { labelKey: "messaging.deliveryData.linkClicked", get label() { return i18nT("messaging.deliveryData.linkClicked"); }, tone: "primary", icon: "link", blurb: "Recipient clicked a link in the message." },
  soft_bounce: { labelKey: "messaging.deliveryData.softBounce", get label() { return i18nT("messaging.deliveryData.softBounce"); }, tone: "warn", icon: "warn", blurb: "Temporary failure — mailbox full or server busy. Will retry." },
  hard_bounce: { labelKey: "messaging.deliveryData.hardBounce", get label() { return i18nT("messaging.deliveryData.hardBounce"); }, tone: "danger", icon: "warn", blurb: "Permanent failure — address invalid or rejected. Suppressed." },
  // Notification-history lattice (listMessageHistory / getMessageHistoryDetail).
  queued: { labelKey: "messaging.deliveryData.queued", get label() { return i18nT("messaging.deliveryData.queued"); }, tone: "neutral", icon: "send", blurb: "Queued for sending — no provider events yet." },
  accepted: { labelKey: "messaging.deliveryData.accepted", get label() { return i18nT("messaging.deliveryData.accepted"); }, tone: "neutral", icon: "send", blurb: "Accepted by the sending provider." },
  deferred: { labelKey: "messaging.deliveryData.deferred", get label() { return i18nT("messaging.deliveryData.deferred"); }, tone: "warn", icon: "warn", blurb: "Temporary failure — the provider will retry." },
  bounced: { labelKey: "messaging.deliveryData.bounced", get label() { return i18nT("messaging.deliveryData.bounced"); }, tone: "danger", icon: "warn", blurb: "Permanent delivery failure — the address was rejected." },
  complained: { labelKey: "messaging.deliveryData.complaint", get label() { return i18nT("messaging.deliveryData.complaint"); }, tone: "danger", icon: "warn", blurb: "Recipient marked the message as spam." },
  dropped: { labelKey: "messaging.deliveryData.dropped", get label() { return i18nT("messaging.deliveryData.dropped"); }, tone: "danger", icon: "warn", blurb: "Dropped by the provider before delivery." },
  unsubscribed: { labelKey: "messaging.deliveryData.unsubscribed", get label() { return i18nT("messaging.deliveryData.unsubscribed"); }, tone: "neutral", icon: "x", blurb: "Recipient unsubscribed from this stream." },
  failed: { labelKey: "common.status.failed", get label() { return i18nT("common.status.failed"); }, tone: "danger", icon: "warn", blurb: "The send failed." },
  suppressed: { labelKey: "messaging.deliveryData.suppressed", get label() { return i18nT("messaging.deliveryData.suppressed"); }, tone: "warn", icon: "x", blurb: "Not sent — the recipient is on the suppression list." },
};

export const DELIV_SOURCES: Record<string, { icon: string; color: string }> = {
  flow: { icon: "send", color: "#5142C7" },
  campaign: { icon: "chart", color: "#2D60EB" },
  ticketgroup: { icon: "ticket", color: "#916D0F" },
  onsite: { icon: "qr", color: "#027D47" },
  manual: { icon: "user", color: "#697180" },
  trigger: { icon: "refresh", color: "#0E7490" },
};

export const SOURCE_FILTER_LABELS: Record<string, string> = {
  flow: "Flows", campaign: "Campaigns", ticketgroup: "Ticket-tier flows", onsite: "On-site", manual: "Manual sends", trigger: "Triggers",
};

const _DMON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function delivTimeVal(s: string): number {
  const m = /(\d{1,2})\s+([A-Za-z]{3}),?\s+(\d{1,2}):(\d{2}):(\d{2})/.exec(s || "");
  if (!m) return 0;
  return ((_DMON.indexOf(m[2]) * 31 + +m[1]) * 24 + +m[3]) * 3600 + +m[4] * 60 + +m[5];
}

export const lastEvent = (d: DeliveryRow): DelivEvent => d.events[d.events.length - 1];

export function sortDeliveries(arr: DeliveryRow[], sort: { key: string; dir: "asc" | "desc" }): DeliveryRow[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  const val = (d: DeliveryRow): string | number => {
    switch (sort.key) {
      case "recipient": return d.recipient.name.toLowerCase();
      case "channel": return MSG_CHANNEL(d.channel).name.toLowerCase();
      case "message": return (d.subject || d.text || "").toLowerCase();
      case "sender": return d.sender.name.toLowerCase();
      case "source": return d.source.label.toLowerCase();
      case "latest": default: return delivTimeVal(lastEvent(d).at);
    }
  };
  return arr.slice().sort((a, b) => {
    const va = val(a), vb = val(b);
    if (typeof va === "number") return (va - (vb as number)) * dir;
    return (va < vb ? -1 : va > vb ? 1 : 0) * dir;
  });
}
